import {is} from 'immutable';
import React from 'react';
import _ from 'underscore';

import {LineRanges} from 'analyses/alignment/GapSequenceWrapper';
import {AlignmentAnalysis, Alignment, ImmutableWrapper} from 'analyses/Analyses';
import {assertEqual} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {removeGaps} from 'biojs/Util';
import {BufferViewLayout} from 'buffers/BufferComps';
import {Sequence} from 'components/Sequence';
import {
  DNAAlignmentKind,
  DNAAlignmentAnalysisJSONAttributes,
  AlignmentLocation,
} from 'constants/AlignmentConstants';
import {Strand} from 'constants/MolbioConstants';
import AlignmentSelectionProps from 'molbio/alignments/backbone/AlignmentSelectionProps';
import {PolymerAlignmentTabState} from 'molbio/maps/alignment/PolymerAlignmentTabView';
import {ScrollData} from 'molbio/maps/linear/InfiniteScroller';
import {Line} from 'molbio/maps/linear/LinearLayout';
import Slice from 'molbio/maps/linear/Slice';
import {
  AlignmentMapState,
  AlignmentMapStates,
  SummaryMapState,
  SummaryMapSingleState,
} from 'molbio/maps/MapStates';
import Range from 'molbio/maps/selectables/Range';
import {Selection} from 'molbio/maps/selectables/Selection';
import Type from 'molbio/maps/Type';
import PolymerMapTabState, {PolymerMapTabStateAttributes} from 'molbio/tabstates/PolymerMapTabState';
import {TabStateDefaults} from 'Navigation';
import notifications from 'notifications';
import * as vo from 'settings/visibility-options/VisibilityOptions';
import {constructOptions, READING_FRAMES} from 'settings/visibility-options/VisibilityOptions';
import {Optional} from 'util/Types';
import {isRevert} from 'util/VersionHistoryUtil';

export interface SequenceAlignmentTabStateAttributes extends PolymerMapTabStateAttributes<Sequence> {
  sequence: Sequence;
  analysis: null | AlignmentAnalysis;
  analysisId: null | string;
  alignmentId: null | string;
  loaded: boolean;
  mapstates: AlignmentMapStates;
  range: null | Range;
  slice: Slice | null;
  scrollData: null | ScrollData;
  selectionProps: AlignmentSelectionProps;
  dnaType: Type;
  summaryState: null | SummaryMapState;
  visibilityOptions: vo.VisibilityOptions;
  bufferViewLayout: BufferViewLayout;
  selection: Selection;
}

interface SequenceAlignmentTabState {
  get<K extends keyof SequenceAlignmentTabStateAttributes>(key: K): SequenceAlignmentTabStateAttributes[K];
  set<K extends keyof SequenceAlignmentTabStateAttributes>(
    attributeName: K,
    value: SequenceAlignmentTabStateAttributes[K],
    options?: Backbone.ModelSetOptions
  ): SequenceAlignmentTabState;
  set(obj: Partial<SequenceAlignmentTabStateAttributes>): SequenceAlignmentTabState;
}

interface HistoryState {
  analysisState: ImmutableWrapper<DNAAlignmentAnalysisJSONAttributes>;
  selectionStart: number;
  rowIndex: number;
}

class SequenceAlignmentTabState
  extends PolymerMapTabState<Sequence>
  implements PolymerAlignmentTabState<SummaryMapSingleState>
{
  history!: Array<HistoryState>;

  override initialize(attributes: Partial<SequenceAlignmentTabStateAttributes>, options?: {}): void {
    super.initialize(attributes, options);
    this.history = [];
    if (BenchlingConfig.MOLBIO_ENABLE_ALIGNMENT_UNDO) {
      this.on('change:loaded', () => {
        this.initializeHistory();
      });
    }
  }

  initializeHistory(): void {
    if (this.history.length === 0) {
      this.history.push(this.toHistoryState());
    }
  }

  addToHistory(): void {
    const newState = this.toHistoryState();
    // eslint-disable-next-line no-restricted-syntax
    const oldState = _.last(this.history)!;
    // @ts-expect-error: types from Immutable package have been difficult to type
    if (!is(oldState.analysisState.get('alignments'), newState.analysisState.get('alignments'))) {
      this.history.push(newState);
    }
  }

  replaceHistory(newHistory: Array<HistoryState>): void {
    this.history = newHistory;
  }

  toHistoryState(): HistoryState {
    return {
      analysisState: this.get('analysis')!.toHistoryState(),
      selectionStart: this.get('selectionProps').get('start'),
      rowIndex: this.get('selectionProps').get('rowIndex'),
    };
  }

  /**
   * Returns true if the alignment's versionId matches the latest version of the master sequence OR if the
   * latest version is a revert to the alignment's version, false otherwise.
   */
  inSync(): boolean {
    const versions = this.get('sequence').get('versions');
    const lastVersionId = versions.getLastId();
    const analysisVersionId = this.get('analysis')!.get('versionId');

    if (lastVersionId === analysisVersionId) {
      return true;
    }

    const lastVersion = versions.filter((v) => v.id === lastVersionId)[0];
    return (
      isRevert(
        versions.models.map((m) => m.attributes),
        lastVersion.attributes
      ) && lastVersion.get('parentId') === analysisVersionId
    );
  }

  canUndo(): boolean {
    // Check that the master sequence is in sync and history contains changes
    return this.inSync() && this.history.length > 1;
  }

  async revertMasterSequence(previousAnalysisJSON: DNAAlignmentAnalysisJSONAttributes): Promise<boolean> {
    const sequenceFromHistory = previousAnalysisJSON.alignments.find((alignment) =>
      ['template', 'consensus'].includes(alignment.type)
    )!.sequence;
    // This should be OK even with sequences that had old style parts,
    // because we need to have saved over them with new style parts to be able to revert.
    assertEqual(sequenceFromHistory.parts.length, 0, 'Attempted to revert master sequence with parts');
    const basesFromHistory = removeGaps(sequenceFromHistory.bases);
    const masterSequence = this.get('sequence');
    const versionToResetTo = await masterSequence.getVersionToResetTo();

    if (!versionToResetTo) {
      notifications.warn('Cannot undo while previous operation is in progress. Try again in a moment.');
      return false;
    }
    if (versionToResetTo.get('bases') !== basesFromHistory) {
      notifications.error(
        `Could not perform undo because ${this.get('analysis')!.get(
          'alignmentType'
        )} sequence version history is out of sync with alignment history.`
      );
      return false;
    }

    await masterSequence.resetToVersion(versionToResetTo);
    return true;
  }

  // Reverts to the most recent state in history and returns the location of the diff
  async undo(): Promise<[number, number] | []> {
    const analysis = this.get('analysis')!;
    const currentState = this.history.pop()!;
    // eslint-disable-next-line no-restricted-syntax
    const previousState = _.last(this.history)!;
    const previousAnalysisJSON = previousState.analysisState.toJS();
    const masterSequence = this.get('sequence');

    // Check if the master sequence needs to be reverted
    if (currentState.analysisState.toJS().versionId !== previousAnalysisJSON.versionId) {
      const reverted = await this.revertMasterSequence(previousAnalysisJSON);
      if (!reverted) {
        return [];
      }
    }

    previousAnalysisJSON.versionId = masterSequence.getActiveVersionId();
    analysis.set(analysis.parse(previousAnalysisJSON)!);
    this.loadAnalysis(analysis);

    return [currentState.rowIndex, currentState.selectionStart];
  }

  override cleanUp(): void {
    this.stopListening();
    this.set('loaded', false); // Fixes BNCH-35139.
    __guard__(this.get('analysis'), (x) => {
      x.unload();
    });
  }

  // ms stands for "mapstate"
  msVisibilityOptions(alignment: Alignment): vo.VisibilityOptions {
    const editableOptions = [
      vo.MAXIMISE.name,
      vo.SEQUENCE_ALIGNMENT_AXIS.name,
      vo.GAPTOREALAXIS.name,
      vo.DNA.name,
      vo.TRANSLATIONS.name,
      vo.AA_INDICES.name,
      vo.ALIGNMENT_BASE_QUALITY_CAPITALIZATION.name,
    ];
    if (alignment.get('trace') != null) {
      editableOptions.push(vo.TRACE.name);
      editableOptions.push(vo.TRACE_QUALITY.name);
    } else {
      editableOptions.push(vo.ANNOTATIONS.name);
      editableOptions.push(vo.PRIMERS.name);
    }
    if (alignment.get('type') === this.get('analysis')!.get('alignmentType')) {
      editableOptions.push(vo.ALIGNMENT_COMMENTS.name);
    } else if (this.get('analysis')!.get('alignmentType') === DNAAlignmentKind.TEMPLATE) {
      editableOptions.push(vo.ALIGNMENT_ROW_STATS.name);
    }
    return this.get('visibilityOptions').makeLocalSync({editableOptions});
  }

  topVisibilityOptions(): vo.VisibilityOptions {
    return this.get('visibilityOptions').makeLocalSync({
      editableOptions: [
        vo.MAXIMISE.name,
        vo.ANNOTATIONS.name,
        vo.TRANSLATIONS.name,
        vo.AA_INDICES.name,
        vo.PRIMERS.name,
        vo.SEQUENCE_ALIGNMENT_AXIS.name,
        vo.GAPTOREALAXIS.name,
        vo.VOTES.name,
        vo.ALIGNMENT_COMMENTS.name,
        vo.EXPAND_MINI_MAP.name,
      ],
    });
  }

  summaryVisibilityOptions(): vo.VisibilityOptions {
    return this.get('visibilityOptions').makeLocalSync({editableOptions: []});
  }

  override defaults(): TabStateDefaults & Partial<SequenceAlignmentTabStateAttributes> {
    return {
      ...super.defaults(),
      analysis: null,
      analysisId: null,
      alignmentId: null,
      loaded: false,
      mapstates: new AlignmentMapStates(),
      title: 'Alignment Loading...',
      range: null,
      slice: null,
      scrollData: null,
      selectionProps: new AlignmentSelectionProps(),
      // @ts-expect-error: Type expects some constructor args
      dnaType: new Type(),
      summaryState: null,
      visibilityOptions: constructOptions('alignment', [
        {base: vo.ANNOTATIONS, visible: true, editable: true, sync: true},
        {base: vo.TRANSLATIONS, visible: true, editable: true, sync: true},
        {base: vo.AA_INDICES, visible: true, editable: true, sync: true},
        {
          base: READING_FRAMES,
          visible: false,
          editable: true,
        },
        {base: vo.PRIMERS, visible: true, editable: true, sync: true},
        {base: vo.CUTSITES, visible: false, editable: false, sync: true},
        {base: vo.COMPLEMENT, visible: false, editable: false},
        {base: vo.SEQUENCE_ALIGNMENT_AXIS, visible: false, editable: true},
        {base: vo.GAPTOREALAXIS, visible: false, editable: true},
        {base: vo.OVERHANGS, visible: false, editable: false},
        {base: vo.DNA, visible: true, editable: true},
        {base: vo.TRACE, visible: true, editable: true},
        {base: vo.TRACE_QUALITY, visible: true, editable: true},
        {base: vo.ALIGNMENT_BASE_QUALITY_CAPITALIZATION, visible: false, editable: true},
        {base: vo.VOTES, visible: true, editable: true},
        {base: vo.LINE, visible: true, editable: false},
        {base: vo.SEARCH_BASES, visible: true, editable: false},
        {base: vo.ALIGNMENT_ROW_STATS, visible: true, editable: true, sync: true},
        {base: vo.ALIGNMENT_COMMENTS, visible: true, editable: true},
        {base: vo.EXPAND_MINI_MAP, visible: true, editable: true, sync: true},
        {base: vo.MAXIMISE, visible: true, editable: true, sync: false},
      ]),
    };
  }

  handleCanEdit(isMasterSequence: boolean, {isFeatureEdit}: {isFeatureEdit?: boolean} = {}): boolean {
    if (isMasterSequence && !isFeatureEdit) {
      if (this.get('sequence').parts().length) {
        notifications.error('Editing is not currently allowed on sequences with parts.');
        return false;
      }
    }
    if (this.get('sequence').get('versions').getLastId() !== this.get('analysis')!.get('versionId')) {
      const version = this.get('sequence').get('versions').get(this.get('analysis')!.get('versionId'));
      if (version != null) {
        notifications.error(
          `This analysis is out of sync. To edit it, please reset your sequence to version '${version.get(
            'name'
          )}' or realign.`
        );
      } else {
        notifications.error(
          'This analysis is out of sync. ' +
            'Your sequence has been reverted back too far. To edit it, please realign.'
        );
      }
      return false;
    }
    const needsWriteAccess = isMasterSequence && !isFeatureEdit;
    const permissions = this.get('sequence').permissions();
    const hasAccess = needsWriteAccess ? permissions.writable : permissions.polymerFeaturesEditable;
    if (!hasAccess || this.get('sequence').get('_sequence').get('locked')) {
      notifications.error('This sequence is not editable.');
      return false;
    }
    if (!this.get('sequence').get('currentVersion')) {
      notifications.error('Please reset the sequence to the current version before editing this analysis.');
      return false;
    }
    return true;
  }

  async createAllTranslation(strand: Strand): Promise<void> {
    const start = this.get('selectionProps').get('start');
    const end = this.get('selectionProps').get('end');
    const baseMapState = this.get('mapstates').find((mapState) =>
      mapState.get('sequence').has('baseSequence')
    );
    for (const mapState of this.get('mapstates').models) {
      if (mapState.cid !== baseMapState.cid) {
        const sequence = mapState.get('sequence');
        // translations cannot be added to reverse strand of RNA
        if (strand === 1 || !sequence.isSingleStranded()) {
          await sequence.createTranslation(strand, {bulk: true, start, end});
        }
      }
    }
    const baseSequence = baseMapState.get('sequence');
    if (strand === 1 || !baseSequence.isSingleStranded()) {
      await baseSequence.createTranslation(strand, {start, end});
    }
    let name = this.get('analysis')!.get('name');
    if (name.length > 32) {
      name = `${name.slice(0, 32)}...`;
    }
    notifications.success(
      <>
        Sequence alignment <strong>{name}</strong> updated.
      </>
    );
  }

  containsRnaSequence(): boolean {
    const sequenceModels = this.get('mapstates').models.map((mapState) => mapState.get('sequence'));
    return sequenceModels.some((seq) => seq.isRNASequence());
  }

  loadAnalysis(analysis: AlignmentAnalysis): void {
    this.set({loaded: false});
    this.set('analysis', analysis);
    this.set('analysisId', analysis.id);
    this.set('title', this.get('analysis')!.get('name'));

    this.get('mapstates').reset(
      analysis.get('alignments').models.map((alignment: Alignment): AlignmentMapState => {
        let mapstate: AlignmentMapState;
        if (alignment.get('type') === analysis.get('alignmentType')) {
          // This is the consensus or template row, so special-case the attributes.
          mapstate = new AlignmentMapState({
            alignment,
            alignmentComments: analysis.get('alignmentComments'),
            baseSequence: this.get('sequence'),
            visibilityOptions: this.topVisibilityOptions(),
            handleCanEdit: this.handleCanEdit.bind(this, true),
            isInSync: this.inSync.bind(this, true),
            permissions: this.get('sequence').get('permissions'),
          });
        } else {
          mapstate = new AlignmentMapState({
            alignment,
            visibilityOptions: this.msVisibilityOptions(alignment),
            handleCanEdit: this.handleCanEdit.bind(this, false),
            isInSync: () => false,
            permissions: this.get('sequence').get('permissions'),
          });
        }
        // Link together all of the Type instances.
        mapstate.get('layout').DNA.set('type', this.get('dnaType'));
        mapstate.get('layout').Translations.get('single').set('type', this.get('dnaType'));
        mapstate.get('layout').Translations.set('indexType', this.get('dnaType'));
        return mapstate;
      })
    );

    const sequence = this.get('mapstates').models[0].get('sequence'); // TODO: select consensus or whatever
    const sequences = this.get('mapstates').models.map((mapstate) => mapstate.get('sequence'));
    this.set(
      'summaryState',
      new SummaryMapState({
        sequences,
        visibilityOptions: this.summaryVisibilityOptions(),
        alignmentComments: analysis.get('alignmentComments'),
      })
    );
    this.set(
      'range',
      new Range({
        getLengthFn() {
          return sequence.length();
        },
        start: 0,
        end: Math.min(100, sequence.length()),
      })
    );
    this.set('slice', new Slice());
    this.get('selectionProps').set({
      start: 0,
      end: null,
      anchor: 'start',
      active: false,
      rowIndex: 0,
    });
    this.set('loaded', true);

    this.listenTo(this.get('analysis')!.get('alignments'), 'remove', (model: Alignment): void => {
      // Remove mapstates from TabState
      _.chain(this.get('mapstates').models)
        .filter(
          (alignmentMapState) =>
            alignmentMapState.get('sequence').get('_sequence').cid === model.get('sequence').cid
        )
        .each((alignmentMapState) => alignmentMapState.destroy());

      _.chain(this.get('summaryState')!.get('submapstates').models)
        .filter(
          (submapstate) => submapstate.get('sequence').get('_sequence').cid === model.get('sequence').cid
        )
        .each((submapstate) => submapstate.destroy());
    });

    return this.listenTo(this.get('analysis')!.get('alignments'), 'sort', () => {
      // Update the summary submapstates, and the mapstates to match the analysis.
      const order: {[cid: string]: number} = {};
      for (const [i, alignment] of this.get('analysis')!.get('alignments').models.entries()) {
        order[alignment.get('sequence').cid] = i;
      }

      const mapStates: AlignmentMapStates = this.get('mapstates');
      const submapStates = this.get('summaryState')!.get('submapstates');

      mapStates.comparator = (alignmentMapState: AlignmentMapState): number =>
        order[alignmentMapState.get('sequence').get('_sequence').cid];
      submapStates.comparator = (submapstate: SummaryMapSingleState): number =>
        order[submapstate.get('sequence').get('_sequence').cid];

      // eslint-disable-next-line no-restricted-syntax
      mapStates.sort();
      // eslint-disable-next-line no-restricted-syntax
      submapStates.sort();
    });
  }

  getSubmapstates(): Array<SummaryMapSingleState> {
    return this.get('summaryState')!.get('submapstates').models;
  }

  getNameFromSubmapstate(submapstate: SummaryMapSingleState): string {
    return submapstate.get('sequence').name();
  }

  getLineRanges(submapstate: SummaryMapSingleState): LineRanges {
    const sequence = submapstate.get('sequence');
    const masterSequence = this.get('analysis')!.getMasterSequence();
    return sequence.getLineRanges(masterSequence);
  }

  getCidForSubmapstate(submapstate: SummaryMapSingleState): string {
    return submapstate.get('sequence').get('_sequence').cid;
  }

  getMasterSubmapstateCid(): string {
    return this.get('analysis')!.getMasterSequence().cid;
  }

  getSummaryType(): Type {
    return this.get('summaryState')!.get('layout').DNA.get('type');
  }

  fitSummaryTypeToWidth(chars: number, width: number, options?: {allowFractionalSpacing?: boolean}): void {
    this.getSummaryType().fitToWidth(chars, width, options);
  }

  getSummaryMapVisibilityOptions(): vo.VisibilityOptions {
    return this.get('summaryState')!.get('visibilityOptions');
  }

  getSummaryMapLineLayout(): Line {
    return this.getSubmapstates()[0].get('layout').Line;
  }

  setSummaryMapLineLayout({height, vMargin}: {height: number; vMargin: number}): void {
    this.getSubmapstates().forEach((submapstate) => {
      submapstate.get('layout').Line.set({height, vMargin});
    });
  }

  getPolymerLength(): number {
    return this.get('analysis')!.getMasterSequence().length();
  }

  getType(): Type {
    // NB: Depending on the fact that the type is synchronized across all rows.
    return this.get('mapstates').models[0].get('layout').DNA.get('type');
  }

  getMapStateForMasterSequence(): AlignmentMapState {
    return this.get('mapstates').find((ms) => ms.isMasterSequence());
  }

  jumpToLocation({residueIndex, rowIndex}: AlignmentLocation): void {
    const masterSequence = this.getMapStateForMasterSequence().get('sequence');
    masterSequence.get('selection').reset(residueIndex);
    this.get('mapstates').at(rowIndex).get('sequence').trigger('jump', residueIndex);
  }
}

function __guard__<T, V>(value: Optional<T>, transform: (t: T) => V): V | undefined {
  return value != null ? transform(value) : undefined;
}

export default SequenceAlignmentTabState;
